import React from 'react';
import type { Key } from '@heroui/react';
import { PRODUCT_FILTER_DESCRIPTORS } from '../productFilterDescriptors';
import type { CategoryFilterOption } from '../productsMeta';
import {
    applyCategoryPickerChange,
    buildCategoryTree,
    categorySelectionKey,
    describeCategorySelection,
    getCategoryTickStates,
    removeCategorySelection,
    searchCategoryTree,
    toCategoryPickerKeys,
    type CategoryFilterValue,
    type CategorySearchGroup,
    type CategorySelectionItem,
    type CategoryTickState,
    type CategoryTree,
} from './categoryPickerMeta';

export type UseCategoryFilterPickerResult = {
    tree: CategoryTree;
    tickStates: ReadonlyMap<string, CategoryTickState>;
    /** The field's selection: every ticked row, and ids the tree doesn't know. */
    pickedKeys: string[];
    /** The picked values as the field's tags list them, each with its tag key. */
    selection: { key: string; item: CategorySelectionItem }[];
    search: string;
    onSearchChange: (search: string) => void;
    /** The matches, grouped; `null` while nothing is typed. */
    results: { groups: CategorySearchGroup[]; count: number } | null;
    /** Closing the popover forgets the search. */
    onOpenChange: (isOpen: boolean) => void;
    /** The field's new selection, read as the rows ticked or unticked. */
    onSelectionChange: (keys: unknown) => void;
    /** A tag's ×. */
    onRemove: (keys: Set<Key>) => void;
    onClear: () => void;
};

/**
 * The category picker's state (BOS-202): the tree built from the flat option
 * list, each row's tick state, the search, and the field's selection read back
 * into the drawer's category fields. The tree logic itself is
 * `categoryPickerMeta`'s.
 *
 * The search is held here rather than left to the Autocomplete's own filter,
 * because a search changes the list's shape, not only which rows it keeps.
 */
export const useCategoryFilterPicker = (
    items: readonly CategoryFilterOption[],
    value: CategoryFilterValue,
    onChange: (value: CategoryFilterValue) => void,
): UseCategoryFilterPickerResult => {
    const [search, setSearch] = React.useState('');

    const tree = React.useMemo(() => buildCategoryTree(items), [items]);
    const tickStates = React.useMemo(
        () => getCategoryTickStates(value.categoryIds, tree),
        [tree, value.categoryIds],
    );
    const results = React.useMemo(
        () =>
            search.trim().length > 0 ? searchCategoryTree(tree, search) : null,
        [search, tree],
    );
    const selection = describeCategorySelection(value, tree).map((item) => ({
        key: categorySelectionKey(item),
        item,
    }));

    const onOpenChange = React.useCallback((isOpen: boolean) => {
        if (!isOpen) {
            setSearch('');
        }
    }, []);

    return {
        tree,
        tickStates,
        pickedKeys: toCategoryPickerKeys(value, tree),
        selection,
        search,
        onSearchChange: setSearch,
        results,
        onOpenChange,
        onSelectionChange: (keys) =>
            onChange(
                applyCategoryPickerChange(
                    value,
                    Array.isArray(keys) ? keys : [],
                    tree,
                ),
            ),
        onRemove: (keys) =>
            onChange(removeCategorySelection(value, keys, tree)),
        onClear: () => onChange(PRODUCT_FILTER_DESCRIPTORS.category.cleared),
    };
};
